"use client";

import { useSyncStatus, useResourceRowCount } from "@stockline/web";
import { usePathname } from "@/hooks/use-pathname-compat";
import { useEffect, useRef } from "react";
import { trackOperational } from "@/lib/analytics";

// Routes where prices are user-visible — the only place a "zero prices" render
// is a real symptom. Elsewhere an empty price store is irrelevant.
function isPriceRoute(pathname: string): boolean {
  return pathname === "/parameters" || pathname.startsWith("/recap");
}

// At most one emit per window per session — this is a detector, not a metric;
// we only need to know the gap happened, not how many React renders it spanned.
const DEBOUNCE_MS = 5 * 60_000;

/**
 * Business-symptom detector: emits `prices.empty_render` when the prices store
 * is `ready` (a full fetch has landed — so this is NOT the cold-start phase)
 * yet holds zero rows while the user sits on a price-bearing route. This is the
 * exact user-facing shape of the Vivo bug (no prices on a healthy session),
 * caught regardless of the upstream cause (Dexie close, lost SSE event, …).
 *
 * Caveat: a tenant that genuinely has no prices configured also reaches
 * ready+0. Per-tenant analysis disambiguates — a tenant that *always* emits is
 * genuinely empty (ignore); one that emits intermittently is hitting the bug.
 *
 * Mounted once inside `AISyncProvider`; returns no UI.
 */
export function PriceEmptyRenderProbe(): null {
  const pathname = usePathname();
  const status = useSyncStatus("prices");
  const priceCount = useResourceRowCount("prices");
  const lastEmitRef = useRef(0);

  useEffect(() => {
    if (!pathname || !isPriceRoute(pathname)) return;
    // `ready` ⇒ lastFullFetchAt != null. Excludes cold/syncing (0 is expected
    // mid-boot) and error (already surfaced elsewhere).
    if (status !== "ready") return;
    // null = Dexie still resolving the count; only act on a real 0.
    if (priceCount !== 0) return;

    const now = Date.now();
    if (now - lastEmitRef.current < DEBOUNCE_MS) return;
    lastEmitRef.current = now;
    trackOperational("prices.empty_render", { route: pathname });
  }, [pathname, status, priceCount]);

  return null;
}
